import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider
} from '@mui/material';

import PaymentService from '../services/paymentService';
import ServiceService from '../../services/services/serviceService';

const PaymentReceiptModal = ({
  open,
  onClose,
  payment,
  summary,
  onDownload,
  downloading = false
}) => {
  const [logoSrc, setLogoSrc] = useState('/logo.png');
  const [clientBalance, setClientBalance] = useState(null);
  const [clientBalanceBefore, setClientBalanceBefore] = useState(null);

  // Load logo
  useEffect(() => {
    const img = new Image();
    img.src = '/logo.png';
    img.onload = () => setLogoSrc('/logo.png');
    img.onerror = () => setLogoSrc('/logo.png');
  }, []);

  // Load client balance from services statistics (same calculation as ServiceListingPage metrics)
  useEffect(() => {
    if (!payment?.client_id || !open) {
      setClientBalance(null);
      setClientBalanceBefore(null);
      return;
    }

    const loadClientBalance = async () => {
      try {
        // Use services statistics (same as ServiceListingPage metrics)
        // This calculates: total_cost_with_vat (from calculate_target_amount) - total_paid (from service.paid_amount)
        const result = await ServiceService.getStatistics({ client_id: payment.client_id });
        if (result.success && result.data) {
          // The balance from services statistics is the current balance (after all payments including this one)
          // This is: total_cost_with_vat - total_paid
          const balanceAfter = parseFloat(result.data.balance || 0);
          
          // Calculate balance before this payment
          // We need to subtract this payment amount from total_paid
          const paymentAmount = parseFloat(payment.amount || 0);
          const totalPaidAfter = parseFloat(result.data.total_paid || 0);
          const totalPaidBefore = totalPaidAfter - paymentAmount;
          const totalCostWithVat = parseFloat(result.data.total_cost_with_vat || 0);
          const balanceBefore = totalCostWithVat - totalPaidBefore;
          
          setClientBalanceBefore(balanceBefore);
          // Always show balance (even if 0 or negative)
          setClientBalance(balanceAfter);
        }
      } catch (err) {
        console.error('Error loading client balance:', err);
      }
    };

    loadClientBalance();
  }, [payment?.client_id, payment?.amount, open]);

  const receiptData = useMemo(() => {
    if (!payment) {
      return null;
    }

    const distributedAmount = summary?.distributedAmount || 0;
    const paymentAmount = parseFloat(payment.amount || 0);
    const remainingAmount = paymentAmount - distributedAmount;
    const hasRemaining = remainingAmount > 0.01; // Small threshold for floating point

    // Only show services description if there's no remaining amount
    const servicesDescription = (!hasRemaining && summary?.services?.length)
      ? summary.services.map(service => `${service.name} (${PaymentService.formatAmount(service.amount)})`).join(', ')
      : null;

    // Determine description: 
    // 1. If balance becomes 0, say "Εξόφληση υπολοίπου"
    // 2. If has unallocated amount (remaining), say "Έναντι υπολοίπου" (don't show services)
    // 3. Otherwise show notes or services
    let description;
    if (clientBalanceBefore !== null && clientBalance !== null && clientBalance <= 0.01 && clientBalanceBefore > 0.01) {
      description = 'Εξόφληση υπολοίπου';
    } else if (hasRemaining) {
      description = 'Έναντι υπολοίπου';
    } else {
      // Only show services if no remaining amount
      description = payment.notes || servicesDescription || 'Πληρωμή υπηρεσιών';
    }

    return {
      receiptNumber: payment.reference_number || `RC-${String(payment.id).padStart(6, '0')}`,
      date: payment.payment_date ? PaymentService.formatDate(payment.payment_date) : '',
      clientName: payment.client_name || '—',
      amount: PaymentService.formatAmount(payment.amount),
      method: PaymentService.formatPaymentMethod(payment.payment_method),
      description,
      referenceNumber: payment.reference_number,
      hasRemaining,
      // Use client balance from statistics (same as metrics) instead of unallocated amount
      remainingBalance: clientBalance !== null ? PaymentService.formatAmount(clientBalance) : null
    };
  }, [payment, summary, clientBalance, clientBalanceBefore]);

  if (!payment || !receiptData) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Απόδειξη Είσπραξης</Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#f3f4f6' }}>
        <Box sx={{
          maxWidth: 700,
          margin: '0 auto',
          backgroundColor: '#fff',
          border: '2px solid #e5e7eb',
          p: { xs: 3, md: 4 }
        }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, borderBottom: '2px solid #d1d5db', pb: 2, mb: 3, gap: 2 }}>
            <Box>
              {logoSrc && (
                <Box sx={{ mb: 1 }}>
                  <img
                    src={logoSrc}
                    alt="logo"
                    style={{ height: '48px', maxWidth: '100%' }}
                    onError={() => {
                      if (logoSrc !== '/logo.png') setLogoSrc('/logo.png');
                    }}
                  />
                </Box>
              )}
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>ΞΑΝΘΟΥ ΧΡΗΣΤΟΣ</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>ΑΦΜ: 148060098 ΔΟΥ: ΡΟΔΟΥ</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>ΥΠΗΡΕΣΙΕΣ ΠΛΗΡΟΦΟΡΙΚΗΣ</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>ΔΗΜΟΚΡΙΤΟΥ 20 ΤΚ:85101</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>ΤΗΛ: +30 6942624846 • Email: info@netgen.gr</Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: 1 }}>ΑΠΟΔΕΙΞΗ ΕΙΣΠΡΑΞΗΣ</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>RECEIPT</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Αριθμός Απόδειξης</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{receiptData.receiptNumber}</Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ημερομηνία</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{receiptData.date}</Typography>
            </Box>
          </Box>

          <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mb: 3 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Παραλήφθηκε από</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{receiptData.clientName}</Typography>
          </Box>

          <Box sx={{
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            textAlign: 'center',
            py: 3,
            mb: 3
          }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Ποσό</Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 700 }}>{receiptData.amount}</Typography>
          </Box>

          <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mb: 3 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Αιτιολογία είσπραξης</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{receiptData.description}</Typography>
          </Box>
          
          {receiptData.remainingBalance !== null && (
            <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mb: 3 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Υπόλοιπο</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{receiptData.remainingBalance}</Typography>
            </Box>
          )}

          <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mb: 3 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Τρόπος Πληρωμής</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{receiptData.method}</Typography>
          </Box>

          {receiptData.referenceNumber && (
            <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mb: 3 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Αριθμός Συναλλαγής</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{receiptData.referenceNumber}</Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography align="center" sx={{ fontSize: 14, color: 'text.secondary' }}>
            NetGen - Ευχαριστούμε για την εμπιστοσύνη σας
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDownload} variant="contained" disabled={downloading}>
          {downloading ? 'Γίνεται λήψη...' : 'Κατέβασμα PDF'}
        </Button>
        <Button onClick={onClose} color="inherit">Κλείσιμο</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentReceiptModal;

